/**
 * Multi-tenant API client for Tutara Enterprise
 * Handles all tenant, organization, and invitation API calls
 */

import { API_BASE_URL } from '@/config/env'
import type {
  CheckEmailRequest,
  CheckEmailResponse,
  CreateTenantRequest,
  JoinTenantRequest,
  RegistrationResponse,
  Tenant,
  UpdateTenantRequest,
  AddDomainRequest,
  VerifyDomainRequest,
  TenantDomain,
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  Invitation,
  SendInvitationRequest,
  InvitationDetails,
  AcceptInvitationRequest,
  Member,
  UpdateMemberRoleRequest,
} from '@/types/multi-tenant';

// API configuration
const AUTH_API_BASE = `${API_BASE_URL}/api/v1/auth`;
const USER_API_BASE = `${API_BASE_URL}/api/v1/users`;
// Standardized multi-tenant API - all MT endpoints under /api/core/mt/{resource}
// Resources: tenants, organizations, invitations, memberships
const CORE_MT_API_BASE = `${API_BASE_URL}/api/core/mt`;
// Admin-approval join-request endpoints live on core-api under /api/v1/tenants/...
const TENANTS_API_BASE = `${API_BASE_URL}/api/v1/tenants`;
const JOIN_REQUEST_API_BASE = `${TENANTS_API_BASE}/join-requests`;

// ─── Join-request types (admin-approval flow) ───────────────────────────────
export type JoinRequestStatus = 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';

export interface JoinRequestStatusResponse {
  status: JoinRequestStatus;
  tenantName: string;
  firstName: string;
  requestedAt: string;
  reviewedAt?: string;
  rejectionReason?: string;
  canResendReminder: boolean;
  nextReminderAvailableAt?: string;
}

export interface JoinRequestResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantName: string;
  status: JoinRequestStatus;
  statusToken: string;
  requestedAt: string;
  expiresAt: string;
}

export interface AdminJoinRequest {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: JoinRequestStatus;
  requestedAt: string;
}

// Raw shape of the backend's InvitationDetailsDTO (flat, no nested org/tenant
// objects) — mapped to the FE `InvitationDetails` shape in getInvitationDetails.
interface InvitationDetailsRaw {
  invitationId?: string;
  tenantName: string;
  organizationName: string;
  email: string;
  invitedName?: string;
  role: string;
  message?: string;
  invitedByName: string;
  invitedByEmail?: string;
  expiresAt: string;
  status?: string;
  expired?: boolean;
  userExists?: boolean;
  organizationId?: string;
  tenantId?: string;
}

// Helper function for API requests with error handling
async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP error! status: ${response.status}`,
      }));

      throw {
        message: errorData.message || errorData.detail || 'An error occurred',
        status: response.status,
        code: errorData.code,
        // Backend BusinessException/handler payloads carry `errorCode` (e.g.
        // FREE_EMAIL_NOT_ALLOWED). Surface it so callers can branch on it.
        errorCode: errorData.errorCode,
      };
    }

    return await response.json();
  } catch (error: any) {
    if (error.message) {
      throw error;
    }

    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw {
        message: 'Unable to connect to the server. Please check your connection.',
        status: 0,
      };
    }

    throw {
      message: 'An unexpected error occurred',
      status: 0,
    };
  }
}

/**
 * Multi-tenant API service
 */
export const multiTenantApi = {
  // ==================== Authentication ====================

  /**
   * Check if tenant exists for email domain
   */
  async checkEmail(request: CheckEmailRequest): Promise<CheckEmailResponse> {
    const response: any = await fetchWithAuth(`${AUTH_API_BASE}/check-email`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    // Map backend response to frontend CheckEmailResponse format
    return {
      found: response.tenantFound || false,
      tenant: response.tenantFound && response.tenantId ? {
        id: response.tenantId,
        name: response.tenantName,
        logoUrl: undefined,
      } : undefined,
      isFreeEmail: !response.domainVerified,
      suggestedTenantName: response.suggestedTenantName,
      domain: response.domain,
    };
  },

  /**
   * Register new user and create new tenant
   */
  async registerWithNewTenant(request: CreateTenantRequest): Promise<RegistrationResponse> {
    const response: any = await fetchWithAuth(`${AUTH_API_BASE}/register`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    // Map backend response to frontend RegistrationResponse format
    return {
      success: response.success,
      user: {
        id: response.userId,
        email: response.email,
        name: response.name || `${response.firstName} ${response.lastName}`.trim(),
      },
      tenant: {
        id: response.tenantId,
        name: response.tenantName,
      },
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    };
  },

  /**
   * Register new user and join existing tenant
   */
  async registerWithExistingTenant(request: JoinTenantRequest): Promise<RegistrationResponse> {
    const response: any = await fetchWithAuth(`${AUTH_API_BASE}/register-with-tenant`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    // Map backend response to frontend RegistrationResponse format
    return {
      success: response.success,
      user: {
        id: response.userId,
        email: response.email,
        name: response.name || `${response.firstName} ${response.lastName}`.trim(),
      },
      tenant: {
        id: response.tenantId,
        name: response.tenantName,
      },
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    };
  },

  // ==================== Tenant Management ====================

  /**
   * Get current user's tenant
   */
  async getCurrentTenant(token: string): Promise<Tenant> {
    return fetchWithAuth<Tenant>(`${CORE_MT_API_BASE}/tenants/current`, {}, token);
  },

  /**
   * Update tenant details
   */
  async updateTenant(
    tenantId: string,
    request: UpdateTenantRequest,
    token: string
  ): Promise<Tenant> {
    return fetchWithAuth<Tenant>(
      `${CORE_MT_API_BASE}/tenants/${tenantId}`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      },
      token
    );
  },

  /**
   * Add domain to tenant
   */
  async addDomain(
    tenantId: string,
    request: AddDomainRequest,
    token: string
  ): Promise<TenantDomain> {
    return fetchWithAuth<TenantDomain>(
      `${CORE_MT_API_BASE}/tenants/${tenantId}/domains`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      token
    );
  },

  /**
   * Send domain verification email
   */
  async verifyDomain(
    tenantId: string,
    domainId: string,
    request: VerifyDomainRequest,
    token: string
  ): Promise<{ success: boolean; message: string }> {
    return fetchWithAuth<{ success: boolean; message: string }>(
      `${CORE_MT_API_BASE}/tenants/${tenantId}/domains/${domainId}/verify`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      token
    );
  },

  /**
   * Remove domain from tenant
   */
  async removeDomain(tenantId: string, domainId: string, token: string): Promise<void> {
    return fetchWithAuth<void>(
      `${CORE_MT_API_BASE}/tenants/${tenantId}/domains/${domainId}`,
      {
        method: 'DELETE',
      },
      token
    );
  },

  // ==================== Organization Management ====================

  /**
   * Get all organizations for tenant
   */
  async getOrganizations(tenantId: string, token: string): Promise<Organization[]> {
    return fetchWithAuth<Organization[]>(
      `${CORE_MT_API_BASE}/tenants/${tenantId}/organizations`,
      {},
      token
    );
  },

  /**
   * Create new organization
   */
  async createOrganization(
    tenantId: string,
    request: CreateOrganizationRequest,
    token: string
  ): Promise<Organization> {
    return fetchWithAuth<Organization>(
      `${CORE_MT_API_BASE}/tenants/${tenantId}/organizations`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      token
    );
  },

  /**
   * Get organization details
   */
  async getOrganization(organizationId: string, token: string): Promise<Organization> {
    return fetchWithAuth<Organization>(`${CORE_MT_API_BASE}/organizations/${organizationId}`, {}, token);
  },

  /**
   * Update organization
   */
  async updateOrganization(
    organizationId: string,
    request: UpdateOrganizationRequest,
    token: string
  ): Promise<Organization> {
    return fetchWithAuth<Organization>(
      `${CORE_MT_API_BASE}/organizations/${organizationId}`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      },
      token
    );
  },

  /**
   * Delete organization
   */
  async deleteOrganization(organizationId: string, token: string): Promise<void> {
    return fetchWithAuth<void>(
      `${CORE_MT_API_BASE}/organizations/${organizationId}`,
      {
        method: 'DELETE',
      },
      token
    );
  },

  /**
   * Get user's organizations
   */
  async getUserOrganizations(token: string): Promise<Organization[]> {
    return fetchWithAuth<Organization[]>(`${USER_API_BASE}/me/organizations`, {}, token);
  },

  // ==================== Member Management ====================

  /**
   * Get organization members
   */
  async getOrganizationMembers(organizationId: string, token: string): Promise<Member[]> {
    return fetchWithAuth<Member[]>(
      `${CORE_MT_API_BASE}/organizations/${organizationId}/members`,
      {},
      token
    );
  },

  /**
   * Update member role
   */
  async updateMemberRole(
    organizationId: string,
    userId: string,
    request: UpdateMemberRoleRequest,
    token: string
  ): Promise<Member> {
    return fetchWithAuth<Member>(
      `${CORE_MT_API_BASE}/organizations/${organizationId}/members/${userId}`,
      {
        method: 'PUT',
        body: JSON.stringify(request),
      },
      token
    );
  },

  /**
   * Remove member from organization
   */
  async removeMember(
    organizationId: string,
    userId: string,
    token: string
  ): Promise<void> {
    return fetchWithAuth<void>(
      `${CORE_MT_API_BASE}/organizations/${organizationId}/members/${userId}`,
      {
        method: 'DELETE',
      },
      token
    );
  },

  // ==================== Invitation Management ====================

  /**
   * Check for pending invitations by email (public endpoint)
   */
  async checkPendingInvitations(email: string): Promise<{
    hasPendingInvitations: boolean;
    invitations: Invitation[];
  }> {
    return fetchWithAuth<{
      hasPendingInvitations: boolean;
      invitations: Invitation[];
    }>(
      `${CORE_MT_API_BASE}/invitations/check`,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  },

  /**
   * Send invitation to join organization
   */
  async sendInvitation(
    organizationId: string,
    request: SendInvitationRequest,
    token: string
  ): Promise<Invitation> {
    return fetchWithAuth<Invitation>(
      `${CORE_MT_API_BASE}/organizations/${organizationId}/invitations`,
      {
        method: 'POST',
        body: JSON.stringify({
          email: request.email,
          role: request.role,
          message: request.personalMessage ?? '',
        }),
      },
      token
    );
  },

  /**
   * Get invitation details by token (public endpoint).
   * Backend returns a flat InvitationDetailsDTO (expired/userExists/message/
   * invitedByName/organizationName/tenantName, no nested objects, no
   * organizationId/tenantId/invitedByEmail) — map it to the FE shape.
   */
  async getInvitationDetails(invitationToken: string): Promise<InvitationDetails> {
    const raw = await fetchWithAuth<InvitationDetailsRaw>(
      `${CORE_MT_API_BASE}/invitations/${invitationToken}`,
      {}
    );
    return {
      token: invitationToken,
      email: raw.email,
      role: raw.role,
      status: raw.status ?? (raw.expired ? 'expired' : 'pending'),
      expiresAt: raw.expiresAt,
      organization: { id: raw.organizationId ?? '', name: raw.organizationName },
      tenant: { id: raw.tenantId ?? '', name: raw.tenantName },
      invitedBy: { name: raw.invitedByName, email: raw.invitedByEmail ?? '' },
      personalMessage: raw.message,
      isExpired: !!raw.expired,
      alreadyRegistered: !!raw.userExists,
      invitedName: raw.invitedName,
    } as InvitationDetails;
  },

  /**
   * Accept invitation.
   * Backend returns an OrganizationMembership (no FE type exported for it,
   * and no tokens) — the caller does not consume the body; it logs the user
   * in separately via authApi.login.
   */
  async acceptInvitation(
    invitationToken: string,
    request: AcceptInvitationRequest
  ): Promise<unknown> {
    return fetchWithAuth<unknown>(
      `${CORE_MT_API_BASE}/invitations/${invitationToken}/accept`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
  },

  /**
   * Revoke invitation (admin only)
   */
  async revokeInvitation(invitationId: string, token: string): Promise<void> {
    return fetchWithAuth<void>(
      `${CORE_MT_API_BASE}/invitations/${invitationId}`,
      {
        method: 'DELETE',
      },
      token
    );
  },

  /**
   * Get all invitations for organization
   */
  async getOrganizationInvitations(
    organizationId: string,
    token: string
  ): Promise<Invitation[]> {
    return fetchWithAuth<Invitation[]>(
      `${CORE_MT_API_BASE}/organizations/${organizationId}/invitations`,
      {},
      token
    );
  },

  // ==================== Join Requests (admin-approval flow) ====================

  /**
   * Submit a join request after OTP verification (public, gateway-handled).
   * The gateway consumes the single-use verificationToken, then forwards the
   * verified email to core-api's internal endpoint.
   */
  async submitJoinRequest(body: {
    email: string;
    firstName: string;
    lastName: string;
    verificationToken: string;
  }): Promise<JoinRequestResponse> {
    return fetchWithAuth<JoinRequestResponse>(`${AUTH_API_BASE}/submit-join-request`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Poll a join request's status by its requester-issued statusToken (public).
   */
  async getJoinRequestStatus(token: string): Promise<JoinRequestStatusResponse> {
    return fetchWithAuth<JoinRequestStatusResponse>(
      `${JOIN_REQUEST_API_BASE}/status?token=${encodeURIComponent(token)}`,
      { method: 'GET' }
    );
  },

  /**
   * Re-notify admins of a pending request (public, rate-limited server-side).
   */
  async resendJoinRequestReminder(token: string): Promise<{ sent: boolean }> {
    return fetchWithAuth<{ sent: boolean }>(
      `${JOIN_REQUEST_API_BASE}/resend-reminder?token=${encodeURIComponent(token)}`,
      { method: 'POST' }
    );
  },

  /**
   * Set the user's password via the magic-link setPasswordToken (public).
   * Creates the Keycloak user + membership server-side.
   */
  async setJoinRequestPassword(body: {
    setPasswordToken: string;
    password: string;
  }): Promise<{ status: string }> {
    return fetchWithAuth<{ status: string }>(`${JOIN_REQUEST_API_BASE}/set-password`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // ---- Admin (require JWT) ----

  async listPendingJoinRequests(tenantId: string, token: string): Promise<AdminJoinRequest[]> {
    return fetchWithAuth<AdminJoinRequest[]>(
      `${TENANTS_API_BASE}/${tenantId}/join-requests`,
      { method: 'GET' },
      token
    );
  },

  async getPendingJoinRequestCount(tenantId: string, token: string): Promise<{ pending: number }> {
    return fetchWithAuth<{ pending: number }>(
      `${TENANTS_API_BASE}/${tenantId}/join-requests/count`,
      { method: 'GET' },
      token
    );
  },

  async approveJoinRequest(id: string, token: string): Promise<JoinRequestResponse> {
    return fetchWithAuth<JoinRequestResponse>(
      `${JOIN_REQUEST_API_BASE}/${id}/approve`,
      { method: 'POST' },
      token
    );
  },

  async rejectJoinRequest(id: string, reason: string, token: string): Promise<JoinRequestResponse> {
    return fetchWithAuth<JoinRequestResponse>(
      `${JOIN_REQUEST_API_BASE}/${id}/reject`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
      token
    );
  },

  async bulkApproveJoinRequests(ids: string[], token: string): Promise<Record<string, string>> {
    return fetchWithAuth<Record<string, string>>(
      `${JOIN_REQUEST_API_BASE}/bulk-approve`,
      {
        method: 'POST',
        body: JSON.stringify({ ids }),
      },
      token
    );
  },

  async bulkRejectJoinRequests(
    ids: string[],
    reason: string,
    token: string
  ): Promise<Record<string, string>> {
    return fetchWithAuth<Record<string, string>>(
      `${JOIN_REQUEST_API_BASE}/bulk-reject`,
      {
        method: 'POST',
        body: JSON.stringify({ ids, reason }),
      },
      token
    );
  },
};

export default multiTenantApi;
